import { Component } from '../core/smpl-components.js'

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { scrollContext } from '../core/scroll-context'

gsap.registerPlugin(ScrollTrigger)

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Portable coupon placer — framework-agnostic, no braspenning-enterprise imports.
// Drop the class into any project; give it a root element and it handles the rest.
//
// On construction it picks one of three placements (first host found wins):
//   - AREA (`.js-coupon-area`)   → moves the coupon into that region and places it at a
//     random spot *inside* the region (absolute, clamped to the region's box).
//   - ANCHOR (`.js-coupon-anchor`) → moves the coupon into that element and renders it
//     in-flow (the page fully owns the position).
//   - FLOATING (no host)         → absolute at a random point on the page, confined to a
//     safe edge band and clamped within the document so it is never off-screen.
// AREA and ANCHOR scroll with their host; FLOATING is absolute in the document, so it
// scrolls with the page too (never `fixed`).
//
// Dismiss is session-scoped (sessionStorage): once closed it stays hidden until the tab
// is closed. A host marked with the force attribute (default `data-coupon-force`) always
// shows (ignores the dismissed flag) and is NOT closable (the close button is hidden).
// Collision-detection against content is deliberately avoided — safe zones + dismiss.
//
// Public API: new Coupon(element, options) · destroy()

const DEFAULTS = {
	areaSelector: '.js-coupon-area',
	anchorSelector: '.js-coupon-anchor',
	linkSelector: '.js-coupon-link',
	closeSelector: '.js-coupon-close',
	forceAttr: 'data-coupon-force', // on the area/anchor host → always show, not closable
	urlAttr: 'couponUrl', // dataset key on the root → element.dataset.couponUrl
	dismissKey: 'coupon-dismissed',
	edgeMargin: 24, // gap kept from every document edge (px), floating mode
	safeBand: 160, // depth of the left/right band random placement is confined to (px)

	// Reveal animation (GSAP + ScrollTrigger) — dynamically placed coupons only (area +
	// floating). The coupon starts faded/scaled-down at 0°, then animates in and towards
	// a random tilt when it scrolls into view.
	// Every placement gets a random tilt: a magnitude in [minRotation, maxRotation]
	// with a random direction, so it's never (nearly) straight.
	minRotation: 5, // deg — smallest tilt magnitude
	maxRotation: 8, // deg — largest tilt magnitude
	revealStart: 'top 85%', // ScrollTrigger start (element top hits 85% of viewport)
	revealDuration: 0.4, // seconds
	revealEase: 'back.out(1.4)', // slight overshoot as it settles onto the tilt

	// Close animation — mirrors the reveal in reverse (fade + scale down + untilt).
	hideDuration: 0.4, // seconds
	hideEase: 'power4.out', // slight anticipation before it leaves
}

class Coupon {
	constructor(element, options) {
		this.element = element
		this.options = Object.assign({}, DEFAULTS, options)

		this.link = this.element.querySelector(this.options.linkSelector)
		this.closeBtn = this.element.querySelector(this.options.closeSelector)

		this.mode = 'floating' // 'area' | 'anchor' | 'floating'
		this.areaEl = null
		this.forced = false
		this.resizeRaf = null
		this.rotation = 0 // random tilt, applied to every placement
		this.revealTween = null // GSAP reveal tween (+ its ScrollTrigger)
		this.hideTween = null // GSAP close tween

		this.onCloseBound = this.onClose.bind(this)
		this.onResizeBound = this.onResize.bind(this)

		this.init()
	}

	init() {
		const area = document.querySelector(this.options.areaSelector)
		const anchor = document.querySelector(this.options.anchorSelector)
		const host = area || anchor
		this.forced = host ? host.hasAttribute(this.options.forceAttr) : false

		// A forced host always shows; otherwise session dismissal wins — do nothing.
		if (!this.forced && this.isDismissed()) return

		if (this.forced) {
			// Not closable: take the close button out of play entirely.
			if (this.closeBtn) this.closeBtn.hidden = true
		} else if (this.closeBtn) {
			this.closeBtn.addEventListener('click', this.onCloseBound)
		}

		if (this.link) {
			this.link.href = this.element.dataset[this.options.urlAttr] || '#'
		}

		if (area) {
			// Random spot inside a page-defined region.
			this.mode = 'area'
			this.areaEl = area
			// The coupon is absolute within the region, so the region needs to be a
			// positioning context. Only force it if the project hasn't already.
			if (getComputedStyle(area).position === 'static') area.style.position = 'relative'
			this.element.style.position = 'absolute'
			area.appendChild(this.element)
			// Faded/scaled start state must be set before reveal so it doesn't flash.
			this.prepareDynamic()
			// Reveal BEFORE measuring — a hidden box has zero width/height.
			this.reveal()
			this.positionInArea()
			this.createReveal()
			window.addEventListener('resize', this.onResizeBound)
		} else if (anchor) {
			// In-flow: hand the element to the page-defined anchor. No positioning to do,
			// but it still gets the random tilt + scroll-in reveal like the others.
			this.mode = 'anchor'
			this.element.style.position = ''
			this.element.style.left = ''
			this.element.style.top = ''
			anchor.appendChild(this.element)
			this.prepareDynamic()
			this.reveal()
			this.createReveal()
		} else {
			// Floating: absolute so it scrolls with the page. The element stays where
			// it is in the DOM (inside the swap container) — its nearest positioned
			// ancestor is the document, so coordinates resolve against the page. Do
			// NOT move it out to <body>: that hides it from whatever swaps the page,
			// which leaks instances and stops it re-rendering on navigation.
			this.mode = 'floating'
			this.element.style.position = 'absolute'
			this.prepareDynamic()
			// Reveal BEFORE measuring — a hidden box has zero width/height, which
			// would make it land half off-screen.
			this.reveal()
			this.positionRandom()
			this.createReveal()
			window.addEventListener('resize', this.onResizeBound)
		}
	}

	destroy() {
		if (this.closeBtn) this.closeBtn.removeEventListener('click', this.onCloseBound)
		window.removeEventListener('resize', this.onResizeBound)
		if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf)
		if (this.revealTween) {
			if (this.revealTween.scrollTrigger) this.revealTween.scrollTrigger.kill()
			this.revealTween.kill()
			this.revealTween = null
		}
		if (this.hideTween) {
			this.hideTween.kill()
			this.hideTween = null
		}
	}

	pickRotation() {
		const { minRotation, maxRotation } = this.options
		const magnitude = minRotation + Math.random() * (maxRotation - minRotation)
		this.rotation = (Math.random() < 0.5 ? -1 : 1) * magnitude
	}

	// Pick the random tilt and set the faded/scaled-down start state (0° so the reveal
	// animates towards the tilt). autoAlpha keeps it invisible without display:none, so
	// it still has dimensions to measure/position.
	prepareDynamic() {
		this.pickRotation()
		gsap.set(this.element, {
			autoAlpha: 0,
			scale: 0.85,
			rotation: 0,
			transformOrigin: 'center center',
		})
	}

	// Animate in when the coupon scrolls into view (GSAP + ScrollTrigger). Uses the
	// active scroller so it also works inside overlays.
	createReveal() {
		if (prefersReducedMotion()) {
			gsap.set(this.element, { autoAlpha: 1, scale: 1, rotation: this.rotation })
			return
		}
		this.revealTween = gsap.to(this.element, {
			autoAlpha: 1,
			scale: 1,
			rotation: this.rotation,
			duration: this.options.revealDuration,
			ease: this.options.revealEase,
			scrollTrigger: {
				trigger: this.element,
				scroller: scrollContext.getScroller(),
				start: this.options.revealStart,
				once: true,
			},
		})
	}

	isDismissed() {
		try {
			return sessionStorage.getItem(this.options.dismissKey) === '1'
		} catch (e) {
			return false
		}
	}

	reveal() {
		this.element.hidden = false
		this.element.setAttribute('aria-hidden', 'false')
	}

	onClose() {
		try {
			sessionStorage.setItem(this.options.dismissKey, '1')
		} catch (e) {
			/* storage unavailable — hide for now regardless */
		}

		// Kill an in-flight reveal so it doesn't fight the close.
		if (this.revealTween) {
			if (this.revealTween.scrollTrigger) this.revealTween.scrollTrigger.kill()
			this.revealTween.kill()
			this.revealTween = null
		}

		if (prefersReducedMotion()) {
			this.hideNow()
			return
		}

		// Reverse of the reveal: fade + scale down + untilt, then take it out of flow.
		this.hideTween = gsap.to(this.element, {
			autoAlpha: 0,
			scale: 0.85,
			rotation: 0,
			duration: this.options.hideDuration,
			ease: this.options.hideEase,
			onComplete: () => this.hideNow(),
		})
	}

	hideNow() {
		this.element.hidden = true
		this.element.setAttribute('aria-hidden', 'true')
	}

	// rAF-throttled: keep the box in bounds when the document resizes, without
	// re-shuffling it to a jarring new spot on every tick.
	onResize() {
		if (this.resizeRaf) return
		this.resizeRaf = requestAnimationFrame(() => {
			this.resizeRaf = null
			if (this.mode === 'floating') this.clampIntoBounds()
			else if (this.mode === 'area') this.clampIntoArea()
		})
	}

	// Region (area) box, in the region's own coordinate space.
	areaBounds() {
		const w = this.element.offsetWidth
		const h = this.element.offsetHeight
		return {
			maxLeft: Math.max(0, this.areaEl.clientWidth - w),
			maxTop: Math.max(0, this.areaEl.clientHeight - h),
		}
	}

	// Random spot fully inside the region.
	positionInArea() {
		const { maxLeft, maxTop } = this.areaBounds()
		this.element.style.left = `${Math.random() * maxLeft}px`
		this.element.style.top = `${Math.random() * maxTop}px`
	}

	clampIntoArea() {
		const { maxLeft, maxTop } = this.areaBounds()
		const left = parseFloat(this.element.style.left) || 0
		const top = parseFloat(this.element.style.top) || 0
		this.element.style.left = `${this.clamp(left, 0, maxLeft)}px`
		this.element.style.top = `${this.clamp(top, 0, maxTop)}px`
	}

	// Document dimensions (scrollable page area), not just the viewport — so the
	// coupon can sit anywhere on the page and scroll with it.
	bounds() {
		const doc = document.documentElement
		const w = this.element.offsetWidth
		const h = this.element.offsetHeight
		const pageW = doc.clientWidth
		const pageH = Math.max(doc.scrollHeight, doc.clientHeight)
		const { edgeMargin } = this.options
		return {
			w,
			h,
			maxLeft: Math.max(edgeMargin, pageW - w - edgeMargin),
			maxTop: Math.max(edgeMargin, pageH - h - edgeMargin),
		}
	}

	// Random point inside a safe edge band, then clamped so the whole box stays
	// within the document. The clamp is applied last, so it always wins.
	positionRandom() {
		const { edgeMargin, safeBand } = this.options
		const { maxLeft, maxTop } = this.bounds()

		// Bias towards the edges: pick the left OR right band horizontally.
		const band = Math.min(safeBand, Math.max(0, (maxLeft - edgeMargin) / 2))
		const left = Math.random() < 0.5 ? edgeMargin + Math.random() * band : maxLeft - Math.random() * band
		const top = edgeMargin + Math.random() * (maxTop - edgeMargin)

		this.element.style.left = `${this.clamp(left, edgeMargin, maxLeft)}px`
		this.element.style.top = `${this.clamp(top, edgeMargin, maxTop)}px`
	}

	clampIntoBounds() {
		const { edgeMargin } = this.options
		const { maxLeft, maxTop } = this.bounds()
		const left = parseFloat(this.element.style.left) || edgeMargin
		const top = parseFloat(this.element.style.top) || edgeMargin
		this.element.style.left = `${this.clamp(left, edgeMargin, maxLeft)}px`
		this.element.style.top = `${this.clamp(top, edgeMargin, maxTop)}px`
	}

	clamp(value, min, max) {
		return Math.min(Math.max(value, min), max)
	}
}

// Thin braspenning-enterprise wrapper. All coupon behaviour lives in the portable
// Coupon class above — no framework imports, so it can be copied to projects that
// don't use this framework — this class only bridges it to the component lifecycle.
export default class extends Component {
	prepare() {
		this.coupon = new Coupon(this.element)
	}
	mount() {}
	unmount() {
		if (this.coupon) this.coupon.destroy()
	}
}
